import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import type { RootState } from "../../store";
import {
  FiltersState,
  Brand,
  BrandMapping,
  CategoryType,
  SuperCategory,
  Category,
  SubCategory,
  ChildCategory,
  PaginationDto,
  brandmappingDto,
  CategoryHierarchy,
  CategoryFilterDto,
} from "./types";

// Helper to get base URL, fallback to localhost if env var not set
const API_BASE_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ECOMMERCE_PERFIX,
]
  .filter(Boolean)
  .join("");

if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("Warning: REACT_APP_API_BASE_URL is not defined.");
}
if (!process.env.REACT_APP_ECOMMERCE_PERFIX) {
  console.warn("Warning: REACT_APP_ECOMMERCE_PERFIX is not defined.");
}
// The /categories/* endpoints wrap responses via a global TransformInterceptor
// ({ success, message, data, timestamp, path }); /filtersapis/* endpoints don't.
// Unwrap defensively so either shape works.
function unwrap<T>(payload: unknown): T {
  if (payload && typeof payload === "object" && "data" in (payload as any)) {
    return (payload as any).data as T;
  }
  return payload as T;
}

const initialState: FiltersState = {
  brands: { data: [], loading: false, error: null },
  brandMappings: { data: [], loading: false, error: null },
  categoryTypes: { data: [], loading: false, error: null },
  superCategories: { data: [], loading: false, error: null },
  parentCategories: { data: [], loading: false, error: null },
  subCategories: { data: [], loading: false, error: null },
  childCategories: { data: [], loading: false, error: null },
  hierarchy: { data: null, loading: false, error: null },
};

// Async Thunks

export const fetchBrands = createAsyncThunk(
  "filters/fetchBrands",
  async (pagination: PaginationDto, { rejectWithValue }) => {
    try {
      const response = await axios.get<Brand[]>(
        `${API_BASE_URL}/filtersapis/brands`,
        { params: pagination },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch brands",
      );
    }
  },
);

export const fetchBrandMappings = createAsyncThunk(
  "filters/fetchBrandMappings",
  async (query: brandmappingDto, { rejectWithValue }) => {
    try {
      const response = await axios.get<BrandMapping[]>(
        `${API_BASE_URL}/filtersapis/brandmapping`,
        { params: query },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch brand mappings",
      );
    }
  },
);

export const fetchCategoryTypes = createAsyncThunk(
  "filters/fetchCategoryTypes",
  async (query: CategoryFilterDto | undefined, { rejectWithValue }) => {
    try {
      const response = await axios.get<unknown>(
        `${API_BASE_URL}/categories/categories-types`,
        { params: query },
      );
      return unwrap<CategoryType[]>(response.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch category types",
      );
    }
  },
);

export const fetchSuperCategories = createAsyncThunk(
  "filters/fetchSuperCategories",
  async (query: CategoryFilterDto | undefined, { rejectWithValue }) => {
    try {
      const response = await axios.get<unknown>(
        `${API_BASE_URL}/categories/super-categories`,
        { params: query },
      );
      return unwrap<SuperCategory[]>(response.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch super categories",
      );
    }
  },
  {
    condition: (_, { getState }) => {
      const { data, loading } = (getState() as RootState).filters.superCategories;
      return data.length === 0 && !loading;
    },
  },
);

export const fetchParentCategories = createAsyncThunk(
  "filters/fetchParentCategories",
  async (query: CategoryFilterDto, { rejectWithValue }) => {
    try {
      const response = await axios.get<unknown>(
        `${API_BASE_URL}/categories/parent-categories`,
        { params: query },
      );
      return unwrap<Category[]>(response.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch parent categories",
      );
    }
  },
);

export const fetchSubCategories = createAsyncThunk(
  "filters/fetchSubCategories",
  async (query: CategoryFilterDto, { rejectWithValue }) => {
    try {
      const response = await axios.get<unknown>(
        `${API_BASE_URL}/categories/sub-categories`,
        { params: query },
      );
      return unwrap<SubCategory[]>(response.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch sub categories",
      );
    }
  },
);

export const fetchChildCategories = createAsyncThunk(
  "filters/fetchChildCategories",
  async (query: CategoryFilterDto, { rejectWithValue }) => {
    try {
      const response = await axios.get<unknown>(
        `${API_BASE_URL}/categories/child-categories`,
        { params: query },
      );
      return unwrap<ChildCategory[]>(response.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch child categories",
      );
    }
  },
);

export const fetchHierarchyByChildCategory = createAsyncThunk(
  "filters/fetchHierarchyByChildCategory",
  async (childCategory: string, { rejectWithValue }) => {
    try {
      const response = await axios.get<unknown>(
        `${API_BASE_URL}/categories/hierarchy-by-child-category`,
        { params: { childCategory } },
      );
      return unwrap<any>(response.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch category hierarchy",
      );
    }
  },
);

const filtersSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.brands.error = null;
      state.brandMappings.error = null;
      state.categoryTypes.error = null;
      state.superCategories.error = null;
      state.parentCategories.error = null;
      state.subCategories.error = null;
      state.childCategories.error = null;
      state.hierarchy.error = null;
    },
    resetFilters: () => initialState,
  },
  extraReducers: (builder) => {
    // Brands
    builder.addCase(fetchBrands.pending, (state) => {
      state.brands.loading = true;
      state.brands.error = null;
    });
    builder.addCase(fetchBrands.fulfilled, (state, action) => {
      state.brands.loading = false;
      state.brands.data = action.payload;
    });
    builder.addCase(fetchBrands.rejected, (state, action) => {
      state.brands.loading = false;
      state.brands.error = action.payload as string;
    });

    // Brand Mappings
    builder.addCase(fetchBrandMappings.pending, (state) => {
      state.brandMappings.loading = true;
      state.brandMappings.error = null;
    });
    builder.addCase(fetchBrandMappings.fulfilled, (state, action) => {
      state.brandMappings.loading = false;
      state.brandMappings.data = action.payload;
    });
    builder.addCase(fetchBrandMappings.rejected, (state, action) => {
      state.brandMappings.loading = false;
      state.brandMappings.error = action.payload as string;
    });

    // Category Types
    builder.addCase(fetchCategoryTypes.pending, (state) => {
      state.categoryTypes.loading = true;
      state.categoryTypes.error = null;
    });
    builder.addCase(fetchCategoryTypes.fulfilled, (state, action) => {
      state.categoryTypes.loading = false;
      state.categoryTypes.data = action.payload;
    });
    builder.addCase(fetchCategoryTypes.rejected, (state, action) => {
      state.categoryTypes.loading = false;
      state.categoryTypes.error = action.payload as string;
    });

    // Super Categories
    builder.addCase(fetchSuperCategories.pending, (state) => {
      state.superCategories.loading = true;
      state.superCategories.error = null;
    });
    builder.addCase(fetchSuperCategories.fulfilled, (state, action) => {
      state.superCategories.loading = false;
      state.superCategories.data = action.payload;
    });
    builder.addCase(fetchSuperCategories.rejected, (state, action) => {
      state.superCategories.loading = false;
      state.superCategories.error = action.payload as string;
    });

    // Parent Categories
    builder.addCase(fetchParentCategories.pending, (state) => {
      state.parentCategories.loading = true;
      state.parentCategories.error = null;
    });
    builder.addCase(fetchParentCategories.fulfilled, (state, action) => {
      state.parentCategories.loading = false;
      state.parentCategories.data = action.payload;
    });
    builder.addCase(fetchParentCategories.rejected, (state, action) => {
      state.parentCategories.loading = false;
      state.parentCategories.error = action.payload as string;
    });

    // Sub Categories
    builder.addCase(fetchSubCategories.pending, (state) => {
      state.subCategories.loading = true;
      state.subCategories.error = null;
    });
    builder.addCase(fetchSubCategories.fulfilled, (state, action) => {
      state.subCategories.loading = false;
      state.subCategories.data = action.payload;
    });
    builder.addCase(fetchSubCategories.rejected, (state, action) => {
      state.subCategories.loading = false;
      state.subCategories.error = action.payload as string;
    });

    // Child Categories
    builder.addCase(fetchChildCategories.pending, (state) => {
      state.childCategories.loading = true;
      state.childCategories.error = null;
    });
    builder.addCase(fetchChildCategories.fulfilled, (state, action) => {
      state.childCategories.loading = false;
      state.childCategories.data = action.payload;
    });
    builder.addCase(fetchChildCategories.rejected, (state, action) => {
      state.childCategories.loading = false;
      state.childCategories.error = action.payload as string;
    });

    // Hierarchy
    builder.addCase(fetchHierarchyByChildCategory.pending, (state) => {
      state.hierarchy.loading = true;
      state.hierarchy.error = null;
    });
    builder.addCase(
      fetchHierarchyByChildCategory.fulfilled,
      (state, action) => {
        state.hierarchy.loading = false;
        state.hierarchy.data = action.payload;
      },
    );
    builder.addCase(fetchHierarchyByChildCategory.rejected, (state, action) => {
      state.hierarchy.loading = false;
      state.hierarchy.error = action.payload as string;
    });
  },
});

export const { clearErrors, resetFilters } = filtersSlice.actions;

export default filtersSlice.reducer;
